import { Check, ChevronDown } from "lucide-react";
import { type FocusEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from "react";

export interface ScopeOption {
  value: string;
  label: string;
}

interface ScopeMenuProps {
  label: string;
  value: string;
  options: readonly ScopeOption[];
  onChange: (value: string) => void;
  className?: string;
}

// The workspace switch in the sidebar and the venue switch in the top bar.
//
// A native select draws its own popup, which no stylesheet reaches, so the pill opened into a
// square system menu that matched nothing around it. This is the same control drawn by the app:
// a button that reads as the pill, and a listbox underneath it that shares the pill's chrome.
//
// Keyboard: Enter, Space or an arrow key opens the list and focus moves to the chosen option;
// the arrows, Home and End move between options, Enter or Space chooses, Escape closes and puts
// focus back on the button, Tab closes and moves on. Tests and the e2e fixtures reach the control
// by its accessible name and read the chosen value from data-value, because a button has no
// value of its own.
export function ScopeMenu({ label, value, options, onChange, className }: ScopeMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const listId = useId();
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return;
    optionRefs.current[selectedIndex]?.focus();
    const onPointerDown = (event: Event) => {
      const root = rootRef.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, selectedIndex]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const choose = (next: string) => {
    close(true);
    if (next !== value) onChange(next);
  };

  const focusOption = (index: number) => {
    if (options.length === 0) return;
    optionRefs.current[(index + options.length) % options.length]?.focus();
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const onOptionKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(index + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusOption(options.length - 1);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const option = options[index];
      if (option) choose(option.value);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close(true);
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  // Focus leaving the control closes it, whether it left from the button or from an option. A null
  // relatedTarget means focus went to the document itself (a click on nothing, or the window losing
  // focus), which the pointer listener above already covers, so it is not treated as leaving.
  const onBlur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && !rootRef.current?.contains(next)) setOpen(false);
  };

  return (
    <div ref={rootRef} className={className ? `scope-menu ${className}` : "scope-menu"}>
      <button
        ref={triggerRef}
        type="button"
        className="scope-switch"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        data-value={value}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onTriggerKeyDown}
        onBlur={onBlur}
      >
        <span className="scope-switch-label">{selected?.label ?? label}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open ? (
        <div
          className="scope-menu-list"
          role="listbox"
          id={listId}
          aria-label={label}
          onBlur={onBlur}
        >
          {options.map((option, index) => {
            const active = option.value === value;
            return (
              <div
                key={option.value}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                role="option"
                tabIndex={-1}
                aria-selected={active}
                className={active ? "scope-menu-option active" : "scope-menu-option"}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(option.value)}
                onKeyDown={(event) => onOptionKeyDown(event, index)}
              >
                <span>{option.label}</span>
                {active ? <Check size={16} aria-hidden="true" /> : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
